import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Eye, EyeOff, Lock, RefreshCcw } from 'lucide-react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Logo from "../Assest/images/logo.png"; // Adjust path as needed

const ConvertGuest = () => {
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [guestId, setGuestId] = useState(null);
    const [searchParams] = useSearchParams();
    const returnUrl = searchParams.get("returnUrl") || "/profile";
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [passwordErrors, setPasswordErrors] = useState([]);

    useEffect(() => {
        const token = sessionStorage.getItem('guestOrderToken');

        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                if (decodedToken && decodedToken.guest_id) {
                    setGuestId(decodedToken.guest_id);
                } else {
                    setError('Invalid token format');
                    toast.error('Invalid guest token. Please try again.');
                }
            } catch (err) {
                setError('Failed to decode token');
                toast.error('Failed to process your guest session. Please try again.');
            }
        } else {
            setError('No guest session found');
            toast.error('No guest session found. Please start over.');
        }
    }, []);


    const validatePassword = (password) => {
        const errors = [];
        const strengths = [];

        // Check length
        if (password.length < 8) {
            errors.push("Password must be at least 8 characters long");
        } else {
            strengths.push("Length requirement met");
        }

        // Check for uppercase
        if (!/[A-Z]/.test(password)) {
            errors.push("Must contain at least one uppercase letter");
        } else {
            strengths.push("Uppercase letter included");
        }

        // Check for lowercase
        if (!/[a-z]/.test(password)) {
            errors.push("Must contain at least one lowercase letter");
        } else {
            strengths.push("Lowercase letter included");
        }

        // Check for number
        if (!/[0-9]/.test(password)) {
            errors.push("Must contain at least one number");
        } else {
            strengths.push("Number included");
        }

        // Check for special character
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            errors.push("Must contain at least one special character");
        } else {
            strengths.push("Special character included");
        }

        // Calculate password strength
        const strength = strengths.length;
        setPasswordStrength(strength);
        setPasswordErrors(errors);

        return errors.length === 0;
    };


    const generatePassword = () => {
        const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const lowercase = "abcdefghijklmnopqrstuvwxyz";
        const numbers = "0123456789";
        const symbols = '!@#$%^&*(),.?":{}|<>';

        const getRandomChar = (charset) =>
            charset[Math.floor(Math.random() * charset.length)];

        let password = "";
        // Ensure at least one of each required character type
        password += getRandomChar(uppercase);
        password += getRandomChar(lowercase);
        password += getRandomChar(numbers);
        password += getRandomChar(symbols);

        // Fill the rest of the password
        while (password.length < 12) {
            const allChars = uppercase + lowercase + numbers + symbols;
            password += getRandomChar(allChars);
        }

        // Shuffle the password
        const passwordArray = password.split("");
        for (let i = passwordArray.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [passwordArray[i], passwordArray[j]] = [
                passwordArray[j],
                passwordArray[i],
            ];
        }

        const generatedPassword = passwordArray.join("");

        // Update form data and validate
        setPassword(generatedPassword);
        validatePassword(generatedPassword);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!guestId) {
            setError('Guest ID not found');
            toast.error('Guest session is invalid. Please try again.');
            return;
        }

        setLoading(true);

        try {
            const guestToken = sessionStorage.getItem('guestOrderToken');

            console.log("guestId", guestId);

            const response = await axios.post(`${process.env.REACT_APP_API_URI}/guest/convert-person`, {
                id: guestId,
                password,
                firstName,
                lastName
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${guestToken}`
                }
            });

            if (response.status === 200) {
                sessionStorage.removeItem('guestOrderToken');
                toast.success('Account created successfully!');
                navigate(returnUrl);
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Conversion failed. Please check your information.';
            setError(errorMsg);
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    if (!guestId && error) {
        return (
            <div className="min-h-screen flex bg-gray-100">
                {/* Left side banner */}
                <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#f36812] via-[#ff8a43] to-[#ff9d5c] p-12 flex-col justify-between relative overflow-hidden">
                    {/* Animated background elements */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-0 left-0 w-full h-full">
                            <svg viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
                                <defs>
                                    <linearGradient id="b" gradientTransform="rotate(45 0.5 0.5)">
                                        <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.7" />
                                        <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                                    </linearGradient>
                                </defs>
                                <path
                                    d="M0,1000 C200,800 400,900 500,800 C600,700 700,800 1000,900 L1000,1000 L0,1000 Z"
                                    fill="url(#b)"
                                />
                                <path
                                    d="M0,1000 C300,900 400,850 500,950 C600,1050 800,900 1000,1000 L0,1000 Z"
                                    fill="rgba(255,255,255,0.1)"
                                />
                            </svg>
                        </div>
                    </div>

                    <div className="relative z-10">
                        <div className="brightness-0 invert mb-10">
                            <img src={Logo} alt="vital Source" className="h-12" />
                        </div>
                        <h1 className="text-white text-5xl font-bold leading-tight">
                            Session <br />
                            Expired.
                        </h1>
                        <p className="text-white/90 mt-6 text-lg max-w-md">
                            Your guest session is no longer valid. Please return to home and try again.
                        </p>
                    </div>
                </div>

                {/* Error message */}
                <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                    <div className="w-full max-w-md text-center">
                        <div className="lg:hidden mb-8">
                            <img src={Logo} alt="vital Source" className="h-10 mx-auto" />
                        </div>

                        <div className="bg-red-50 p-8 rounded-lg border border-red-200 mb-6">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-semibold text-red-800 mb-2">Session Error</h2>
                            <p className="text-red-600 mb-4">{error}</p>
                            <button
                                onClick={() => navigate('/')}
                                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-md text-sm font-medium text-white bg-gradient-to-r from-[#f36812] to-[#ff8a43] hover:from-[#d95700] hover:to-[#e67a33] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f36812] transition duration-200"
                            >
                                Return to Home
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex bg-gray-100">
            {/* Left side banner */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#f36812] via-[#ff8a43] to-[#ff9d5c] p-12 flex-col justify-between relative overflow-hidden">
                {/* Animated background elements */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-full h-full">
                        <svg viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                                <linearGradient id="b" gradientTransform="rotate(45 0.5 0.5)">
                                    <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.7" />
                                    <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                                </linearGradient>
                            </defs>
                            <path
                                d="M0,1000 C200,800 400,900 500,800 C600,700 700,800 1000,900 L1000,1000 L0,1000 Z"
                                fill="url(#b)"
                            />
                            <path
                                d="M0,1000 C300,900 400,850 500,950 C600,1050 800,900 1000,1000 L0,1000 Z"
                                fill="rgba(255,255,255,0.1)"
                            />
                        </svg>
                    </div>
                </div>

                <div className="relative z-10">
                    <div className="brightness-0 invert mb-10">
                        <img src={Logo} alt="vital Source" className="h-12" />
                    </div>
                    <h1 className="text-white text-5xl font-bold leading-tight">
                        Complete Your <br />
                        Account.
                    </h1>
                    <p className="text-white/90 mt-6 text-lg max-w-md">
                        Just a few more details to convert your guest account into a full account and unlock all features.
                    </p>
                </div>

                <div className="relative z-10 flex flex-col">
                    <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 mb-10">
                        <div className="flex items-center mb-4">
                            <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center mr-4">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5 text-white"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-white font-semibold">Secure Account</h3>
                                <p className="text-white/70 text-sm">Keep your orders safe</p>
                            </div>
                        </div>
                        <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center mr-4">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5 text-white"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-white font-semibold">Member Benefits</h3>
                                <p className="text-white/70 text-sm">
                                    Access to exclusive features
                                </p>
                            </div>
                        </div>
                    </div>

                    <svg
                        className="w-full"
                        viewBox="0 0 200 20"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fill="rgba(255, 255, 255, 0.2)"
                            d="M0,10 C30,20 70,0 100,10 C130,20 170,0 200,10 L200,20 L0,20 Z"
                        />
                        <path
                            fill="rgba(255, 255, 255, 0.15)"
                            d="M0,15 C40,5 60,15 100,10 C140,5 160,15 200,10 L200,20 L0,20 Z"
                        />
                    </svg>
                </div>
            </div>

            {/* Right side conversion form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    className="w-full max-w-md"
                >
                    <div className="lg:hidden mb-8">
                        <img src={Logo} alt="vital Source" className="h-10 mx-auto" />
                    </div>

                    <div className="text-center mb-10">
                        <h2 className="text-3xl font-bold text-gray-800">Complete Your Account</h2>
                        <p className="text-gray-600 mt-2">
                            Just a few details to convert your guest account
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 block">
                                    First Name
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg
                                            className="h-5 w-5 text-gray-400"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </div>
                                    <input
                                        type="text"
                                        required
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        className="pl-10 w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#f36812] focus:border-transparent bg-white shadow-sm"
                                        placeholder="First name"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 block">
                                    Last Name
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg
                                            className="h-5 w-5 text-gray-400"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </div>
                                    <input
                                        type="text"
                                        required
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        className="pl-10 w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#f36812] focus:border-transparent bg-white shadow-sm"
                                        placeholder="Last name"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 block">
                                Create Password
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    required
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        validatePassword(e.target.value);
                                    }}
                                    placeholder="Create a strong password"
                                    minLength={6}
                                    className="pl-10 w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#f36812] focus:border-transparent bg-white shadow-sm"
                                />
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                    <button
                                        type="button"
                                        onClick={generatePassword}
                                        className="mr-2 text-gray-500 hover:text-gray-700"
                                        title="Generate Password"
                                    >
                                        <RefreshCcw className="h-5 w-5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="text-gray-500 hover:text-gray-700"
                                        title={showPassword ? "Hide Password" : "Show Password"}
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-5 w-5" />
                                        ) : (
                                            <Eye className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>
                            </div>
                            {/* Password Strength Indicator */}
                            <div className="flex h-1.5 space-x-1 mt-1">
                                {[1, 2, 3, 4].map((level) => (
                                    <div
                                        key={level}
                                        className={`flex-1 rounded-full ${passwordStrength >= level
                                                ? level <= 2
                                                    ? "bg-red-500"
                                                    : level <= 3
                                                        ? "bg-yellow-500"
                                                        : "bg-green-500"
                                                : "bg-gray-300"
                                            }`}
                                    ></div>
                                ))}
                            </div>
                            {passwordErrors.length > 0 && (
                                <div className="text-xs text-red-600 mt-1">
                                    {passwordErrors.map((error, index) => (
                                        <p key={index}>• {error}</p>
                                    ))}
                                </div>
                            )}
                        </div>

                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200"
                            >
                                {error}
                            </motion.div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-md text-sm font-medium text-white bg-gradient-to-r from-[#f36812] to-[#ff8a43] hover:from-[#d95700] hover:to-[#e67a33] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f36812] transition duration-200 disabled:opacity-70"
                        >
                            {loading ? (
                                <svg
                                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                            ) : null}
                            <span>
                                {loading ? "Processing..." : "Complete Account"}
                            </span>
                        </button>
                    </form>

                    <div className="mt-8">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-gray-100 text-gray-500">Already have an account?</span>
                            </div>
                        </div>

                        <button
                            onClick={() => navigate('/login')}
                            className="mt-6 w-full py-2.5 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition duration-200"
                        >
                            Sign in instead
                        </button>
                    </div>
                </motion.div>
            </div>
            <ToastContainer />
        </div>
    );
};

export default ConvertGuest;